import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const DuffelLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogIndex, setExpandedLogIndex] = useState(null);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const apiBase = getApiBase();

  useEffect(() => {
    fetchLogs();
  }, [limit]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBase}/duffel-logs?limit=${limit}`, { headers: getAuthHeaders() });
      if (response.data.success) {
        setLogs(response.data.logs);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching Duffel logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all Duffel API logs? This action cannot be undone.')) {
      return;
    }
    try {
      const response = await axios.delete(`${apiBase}/duffel-logs`, { headers: getAuthHeaders() });
      if (response.data.success) {
        alert(`Cleared ${response.data.deleted_count} logs`);
        fetchLogs();
      }
    } catch (error) {
      console.error('Error clearing Duffel logs:', error);
      alert('Error clearing logs.');
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'GET': return 'bg-blue-100 text-blue-800';
      case 'POST': return 'bg-green-100 text-green-800';
      case 'PUT': case 'PATCH': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (code) => {
    if (code >= 200 && code < 300) return 'bg-green-100 text-green-800';
    if (code >= 300 && code < 400) return 'bg-blue-100 text-blue-800';
    if (code >= 400 && code < 500) return 'bg-yellow-100 text-yellow-800';
    if (code >= 500) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatTimestamp = (ts) => ts ? new Date(ts).toLocaleString() : '';

  // Extract a short label from the Duffel URL (e.g. "/air/offer_requests" from full URL)
  const getEndpointLabel = (url) => {
    if (!url) return '';
    try {
      const path = new URL(url).pathname;
      return path;
    } catch {
      return url;
    }
  };

  const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    return (
      <button
        onClick={() => {
          navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-50"
        title="Copy to clipboard"
      >
        {copied ? '✓ Copied' : '📋 Copy'}
      </button>
    );
  };

  const renderPayload = (payload, title) => {
    if (!payload) return null;
    const jsonString = JSON.stringify(payload, null, 2);
    return (
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <h5 className="font-semibold text-sm text-gray-700">{title}:</h5>
          <CopyButton text={jsonString} />
        </div>
        <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto border border-gray-200 max-h-96 overflow-y-auto">
          {jsonString}
        </pre>
      </div>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Duffel API Logs</h1>
          <p className="mt-1 text-sm text-gray-500">
            View Duffel API requests and responses with full payloads
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={25}>Show 25</option>
            <option value={50}>Show 50</option>
            <option value={100}>Show 100</option>
            <option value={200}>Show 200</option>
          </select>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Refresh
          </button>
          <button
            onClick={handleClearLogs}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{logs.length}</span> of{' '}
          <span className="font-semibold">{total}</span> total logs
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">No Duffel API logs yet</p>
            <p className="text-sm mt-2">API calls to Duffel will appear here as they are made</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log, index) => (
              <div key={index} className="p-4 hover:bg-gray-50">
                <div
                  className="cursor-pointer"
                  onClick={() => setExpandedLogIndex(expandedLogIndex === index ? null : index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getMethodColor(log.method)}`}>
                          {log.method}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status_code)}`}>
                          {log.status_code || 'N/A'}
                        </span>
                        {log.duration_ms != null && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            {log.duration_ms}ms
                          </span>
                        )}
                        {log.mode && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${log.mode === 'live' ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}`}>
                            {log.mode}
                          </span>
                        )}
                        {log.error && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                            ERROR
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-mono text-gray-900 break-all mb-1">
                        {getEndpointLabel(log.url)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTimestamp(log.timestamp)}
                        {log.organization_id && <span className="ml-3 text-gray-400">Org: {log.organization_id}</span>}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <svg
                        className={`h-5 w-5 text-gray-400 transform transition-transform ${expandedLogIndex === index ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {expandedLogIndex === index && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    {/* Full URL */}
                    <div className="mb-3 text-xs font-mono text-gray-500 break-all">
                      {log.url}
                    </div>

                    {log.error && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                        <h5 className="font-semibold text-sm text-red-800 mb-1">Error:</h5>
                        <p className="text-sm text-red-700 break-all">{log.error}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-4">
                      {renderPayload(log.request_payload, 'Request Payload')}
                      {renderPayload(log.response_payload, 'Response Payload')}
                    </div>

                    {log.user_id && (
                      <div className="mt-3 text-xs text-gray-500">
                        User: {log.user_id}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {logs.length > 0 && logs.length < total && (
        <div className="text-center text-sm text-gray-500">
          <p>Showing {logs.length} of {total} logs. Increase the limit to see more.</p>
        </div>
      )}
    </div>
  );
};

export default DuffelLogs;
